import { supabase } from '../supabase';
import { useMutation } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Shift, ScheduleShift, SupervisorShift } from '@shiftly/shared';
import { getModelFullName } from '../helpers/modelHelpers';
import { getUserFullName } from '../helpers/userHelpers';

// Shift logging types
export type ShiftLogAction = 
  | 'CREATE'
  | 'UPDATE' 
  | 'DELETE'
  | 'SUBMIT'
  | 'ASSIGN'
  | 'UNASSIGN';

export type ShiftType = 'SALES' | 'SCHEDULING' | 'SUPERVISOR';

export interface ShiftLogData {
  action: ShiftLogAction;
  shift_type: ShiftType;
  shift_id: string;
  performed_by: string;
  details: string;
  changes?: {
    before?: any;
    after?: any;
  };
  metadata?: {
    model_id?: string;
    user_id?: string | null | undefined;
    shift_code?: string | null;
    start_date_time?: DateTime;
    end_date_time?: DateTime;
    group?: string;
    type?: string;
  };
}

const SupabaseShiftLoggingService = {
  async logShiftAction(logData: ShiftLogData): Promise<void> {
    try {
      let changesData: any = null;

      // For CREATE operations, use metadata as the "after" state
      if (logData.action === 'CREATE' && logData.metadata) {
        changesData = {
          after: logData.metadata
        };
      }
      // For UPDATE operations, use the provided changes
      else if (logData.changes) {
        changesData = logData.changes;
      }
      // For other operations, include metadata if available
      else if (logData.metadata) {
        changesData = {
          metadata: logData.metadata
        };
      }

      console.log('Logging shift action:', {
        action: logData.action,
        shift_type: logData.shift_type,
        changesData: changesData
      });

      const { error } = await supabase
        .from('admin_actions')
        .insert({
          type: `${logData.action}_SHIFT`,
          performed_by: logData.performed_by,
          target_id: logData.shift_id,
          target_type: logData.shift_type,
          details: logData.details,
          changes: changesData,
        });

      if (error) {
        console.error('Error logging shift action:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in logShiftAction:', error);
      throw error;
    }
  },

  // Sales shift logging methods
  async logSalesShiftCreate(shift: Shift, performed_by: string): Promise<void> {
    const model_name = await getModelFullName(shift.model_id);
    await this.logShiftAction({
      action: 'CREATE',
      shift_type: 'SALES',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Sales shift created for model ${model_name || shift.model_id}`,
      metadata: {
        model_id: shift.model_id,
        user_id: shift.user_id,
        shift_code: shift.shift_code,
        start_date_time: shift.start_date_time,
        end_date_time: shift.end_date_time,
      },
    });
  },

  async logSalesShiftUpdate(shiftId: string, changes: { before: Partial<Shift>; after: Partial<Shift> }, performed_by: string): Promise<void> {
    await this.logShiftAction({
      action: 'UPDATE',
      shift_type: 'SALES',
      shift_id: shiftId,
      performed_by: performed_by,
      details: `Sales shift updated`,
      changes,
    });
  },

  async logSalesShiftDelete(shift: Shift, performed_by: string): Promise<void> {
    const model_name = await getModelFullName(shift.model_id);
    await this.logShiftAction({
      action: 'DELETE',
      shift_type: 'SALES',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Sales shift deleted for model ${model_name || shift.model_id}`,
      metadata: {
        model_id: shift.model_id,
        user_id: shift.user_id,
        shift_code: shift.shift_code,
      },
    });
  },

  async logSalesShiftSubmit(shift: Shift, performed_by: string): Promise<void> {
    const model_name = await getModelFullName(shift.model_id);
    await this.logShiftAction({
      action: 'SUBMIT',
      shift_type: 'SALES',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Sales shift submitted for model ${model_name || shift.model_id}`,
      metadata: {
        model_id: shift.model_id,
        user_id: shift.user_id,
        shift_code: shift.shift_code,
      },
    });
  },

  // Schedule shift logging methods
  async logScheduleShiftCreate(shift: ScheduleShift, performed_by: string): Promise<void> {
    const model_name = await getModelFullName(shift.model_id);
    await this.logShiftAction({
      action: 'CREATE',
      shift_type: 'SCHEDULING',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Schedule shift created for model ${model_name || shift.model_id}`,
      metadata: shift,
    });
  },

  async logScheduleShiftUpdate(shiftId: string, changes: { before: Partial<ScheduleShift>; after: Partial<ScheduleShift> }, performed_by: string): Promise<void> {
    await this.logShiftAction({
      action: 'UPDATE',
      shift_type: 'SCHEDULING',
      shift_id: shiftId,
      performed_by: performed_by,
      details: `Schedule shift updated`,
      changes,
    });
  },

  async logScheduleShiftDelete(shift: ScheduleShift, performed_by: string): Promise<void> {
    const model_name = await getModelFullName(shift.model_id);
    await this.logShiftAction({
      action: 'DELETE',
      shift_type: 'SCHEDULING',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Schedule shift deleted for model ${model_name || shift.model_id}`,
      changes: {
        before: shift
      },
    });
  },

  async logScheduleShiftAssign(shift: ScheduleShift, performed_by: string): Promise<void> {
    const model_name = await getModelFullName(shift.model_id);
    await this.logShiftAction({
      action: 'ASSIGN',
      shift_type: 'SCHEDULING',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Schedule shift assigned to user for model ${model_name || shift.model_id}`,
      metadata: shift,
    });
  },

  async logScheduleShiftUnassign(shift: ScheduleShift, performed_by: string): Promise<void> {
    const model_name = await getModelFullName(shift.model_id);
    await this.logShiftAction({
      action: 'UNASSIGN',
      shift_type: 'SCHEDULING',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Schedule shift unassigned for model ${model_name || shift.model_id}`,
      metadata: shift,
    });
  },

  // Supervisor shift logging methods
  async logSupervisorShiftCreate(shift: SupervisorShift, performed_by: string): Promise<void> {
    await this.logShiftAction({
      action: 'CREATE',
      shift_type: 'SUPERVISOR',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Supervisor shift created for group ${shift.group}`,
      metadata: shift,
    });
  },

  async logSupervisorShiftUpdate(shiftId: string, changes: { before: Partial<SupervisorShift>; after: Partial<SupervisorShift> }, performed_by: string): Promise<void> {
    await this.logShiftAction({
      action: 'UPDATE',
      shift_type: 'SUPERVISOR',
      shift_id: shiftId,
      performed_by: performed_by,
      details: `Supervisor shift updated`,
      changes,
    });
  },

  async logSupervisorShiftDelete(shift: SupervisorShift, performed_by: string): Promise<void> {
    await this.logShiftAction({
      action: 'DELETE',
      shift_type: 'SUPERVISOR',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Supervisor shift deleted for group ${shift.group}`,
      changes: {
        before: shift
      },
    });
  },

  async logSupervisorShiftSubmit(shift: SupervisorShift, performed_by: string): Promise<void> {
    const user_name = await getUserFullName(shift.user_id || '');
    await this.logShiftAction({
      action: 'SUBMIT',
      shift_type: 'SUPERVISOR',
      shift_id: shift.id,
      performed_by: performed_by,
      details: `Supervisor shift submitted for user ${user_name || shift.user_id}`,
      metadata: shift,
    });
  },
};

// React Query hooks for logging
export const useLogShiftAction = () => {
  return useMutation({
    mutationFn: (logData: ShiftLogData) => 
      SupabaseShiftLoggingService.logShiftAction(logData),
  });
};

export default SupabaseShiftLoggingService;
